import React from 'react';
import { TimePicker } from '@mui/x-date-pickers';

interface ClockTimeFieldProps {
  label: string;
  /** 24-hour "HH:mm" (or "HH:mm:ss"), as the API stores shop hours; empty for none. */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
}

const toDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const [hour, minute] = value.split(':').map(Number);
  return Number.isInteger(hour) && Number.isInteger(minute) ? new Date(2000, 0, 1, hour, minute) : null;
};

const toClock = (date: Date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/**
 * A shop-hours time, picked and shown as US 12-hour time with AM/PM whatever
 * the browser's region (a native `type="time"` input follows the browser, so a
 * UK-set laptop showed 24-hour), and handed back as the 24-hour "HH:mm" the API
 * keeps. A half-typed time stays local until it is complete, so typing never
 * wipes the saved value.
 */
const ClockTimeField: React.FC<ClockTimeFieldProps> = ({ label, value, onChange }) => {
  const [draft, setDraft] = React.useState<Date | null>(() => toDate(value));

  // Follow changes made outside the field (a reset, a load), not our own echo.
  React.useEffect(() => {
    setDraft((current) => {
      const next = toDate(value);
      const same = current && next && !Number.isNaN(current.getTime()) && toClock(current) === toClock(next);
      return same || (!current && !next) ? current : next;
    });
  }, [value]);

  return (
    <TimePicker
      label={label}
      value={draft}
      ampm
      minutesStep={5}
      onChange={(next) => {
        setDraft(next);
        if (!next) onChange(null);
        else if (!Number.isNaN(next.getTime())) onChange(toClock(next));
      }}
      slotProps={{ textField: { fullWidth: true } }}
    />
  );
};

export default ClockTimeField;
